import React, { useMemo } from 'react'
import { Box, Button } from '@mui/material'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import { useAtomValue } from 'jotai/index'
import {
  fileLocationInfo,
  filesMappedToLessonNodeAtom,
  isUpdatingAtom,
  tableUpdateConfigAtom,
  treeIdMapAtom,
  workingTreeAtom,
} from './atoms'
import { useGetTreeById } from '../../../reducers/trees_hooks'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { Table } from 'shared/ui/Table/Table'
import {
  getUpdatesData,
  useDoAllUpdates,
  useDoUpdates,
  useOnlyRequiredUpdates,
} from './utility'
import { openNode } from '../../utility/TreeNavigation'
import { ConfirmCommitButton } from 'shared/ui'

export function GDriveTables(ids: { treeId: string; versionId: string }) {
  useSetAtom(workingTreeAtom)(useGetTreeById(ids))
  return (
    <Box>
      <Box>Ingest GDrive Tables</Box>
      <GoogleLoginButton LoggedInComponent={FilesForLessons} />
    </Box>
  )
}

function FilesForLessons() {
  const columns = useMappingColumns()
  const filesByLessonId = useAtomValue(filesMappedToLessonNodeAtom)

  return (
    <Box>
      <Table
        columns={columns}
        items={filesByLessonId}
        toolBarItems={[
          <UpdateAllButton key="updateAll" />,
          <UpdateRequiredButton key="updateRequired" />,
        ]}
      />
    </Box>
  )
}

function UpdateAllButton() {
  const isUpdating = useAtomValue(isUpdatingAtom)
  const updater = useDoAllUpdates()

  return (
    <ConfirmCommitButton disabled={isUpdating} onClick={updater}>
      Update All
    </ConfirmCommitButton>
  )
}

function UpdateRequiredButton() {
  const isUpdating = useAtomValue(isUpdatingAtom)
  const updater = useOnlyRequiredUpdates()

  return (
    <ConfirmCommitButton disabled={isUpdating} onClick={updater}>
      Update Required
    </ConfirmCommitButton>
  )
}

function useMappingColumns() {
  const isUpdating = useAtomValue(isUpdatingAtom)
  const idMap = useAtomValue(treeIdMapAtom)
  const doUpdates = useDoUpdates(true)
  const updatesConfig = useAtomValue(tableUpdateConfigAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Unit</strong>,
        width: 25,
        rowContent: ({ unitNum }: fileLocationInfo) => {
          return unitNum ?? 'MISSING'
        },
      },
      {
        title: <strong>Lesson</strong>,
        width: 25,
        rowContent: ({ lessonNum }: fileLocationInfo) => {
          return lessonNum ?? 'MISSING'
        },
      },
      {
        title: <strong>Node</strong>,
        width: 100,
        rowContent: ({ nodeId }: fileLocationInfo) => {
          if (!nodeId) return 'MISSING'
          const node = idMap?.[nodeId]
          return (
            <Box>
              {node?.node.title ?? nodeId}
              {node?.node && (
                <Button onClick={() => openNode(node.node, idMap)}>Go</Button>
              )}
            </Box>
          )
        },
      },
      {
        title: <strong>File</strong>,
        width: 100,
        rowContent: ({ file }: fileLocationInfo) => {
          return file?.name ?? 'MISSING'
        },
      },
      {
        title: <strong>Updates</strong>,
        width: 100,
        rowContent: ({ nodeId }: fileLocationInfo) => {
          if (!nodeId) return 'MISSING'
          const node = idMap?.[nodeId]
          if (!node) return 'NO NODE'
          const updates = getUpdatesData(node.node, updatesConfig, true)
          return (
            <Box>
              {updates.map(({ key }, index) => (
                <Box key={`${nodeId}:${key}:${index}`}>{key}</Box>
              ))}
            </Box>
          )
        },
      },
      {
        title: <strong>Do Updates</strong>,
        width: 100,
        rowContent: (data: fileLocationInfo) => {
          const doIt = async () => {
            await doUpdates(data)
          }
          return (
            <Button disabled={isUpdating} onClick={doIt}>
              DoIt
            </Button>
          )
        },
      },
    ]
  }, [idMap, doUpdates, updatesConfig, isUpdating])
}
